import type { History, LocationDescriptor, LocationDescriptorObject, LocationState } from "history";

type CreateHistory<O, H> = (options?: O) => History & H;

function preserveQueryParameters(history: History<unknown>, preserve: string[], location: LocationDescriptorObject): LocationDescriptorObject {
    const currentQuery = new URLSearchParams(history.location.search);
    if (currentQuery) {
        const preservedQuery: { [key: string]: string } = {};
        preserve.forEach((perserveSearchParam) => {
            const v = currentQuery.get(perserveSearchParam);
            if (v) {
                preservedQuery[perserveSearchParam] = v;
            }
        });
        if (location.search) {
            Object.assign(preservedQuery, new URLSearchParams(history.location.search));
        }
        // eslint-disable-next-line no-param-reassign
        location.search = new URLSearchParams(preservedQuery).toString();
    }
    return location;
}

function createLocationDescriptorObject(location: LocationDescriptor, state?: LocationState): LocationDescriptorObject {
    return typeof location === "string" ? { pathname: location, state } : location;
}

export function createPreserveQueryHistory<O, H>(createHistory: CreateHistory<O, H>, queryParameters: string[]): CreateHistory<O, H> {
    return (options?: O) => {
        const history = createHistory(options);
        const oldPush = history.push;
        const oldReplace = history.replace;
        history.push = (path: LocationDescriptor, state?: LocationState) =>
            oldPush.apply(history, [preserveQueryParameters(history, queryParameters, createLocationDescriptorObject(path, state))]);
        history.replace = (path: LocationDescriptor, state?: LocationState) =>
            oldReplace.apply(history, [preserveQueryParameters(history, queryParameters, createLocationDescriptorObject(path, state))]);
        return history;
    };
}
